import { PeerStateView } from "./PeerStateView.js";
export class PeerPresenceInfo {
    ttl;
    #peerStates = new PeerStateView({});
    /**
     * Build a new peer presence state.
     *
     * @param ttl in milliseconds - peers with no activity within this timeframe
     * are forgotten when {@link prune} is called.
     */
    constructor(ttl) {
        this.ttl = ttl;
    }
    has(peerId) {
        return peerId in this.#peerStates.value;
    }
    /**
     * Record that we've seen the given peer recently.
     *
     * @param peerId
     */
    markSeen(peerId) {
        if (!(peerId in this.#peerStates.value)) {
            // Ignore heartbeats from peers we have not seen before: they will send a snapshot
            return;
        }
        this.#peerStates = new PeerStateView({
            ...this.#peerStates.value,
            [peerId]: {
                ...this.#peerStates.value[peerId],
                lastSeenAt: Date.now(),
            },
        });
    }
    /**
     * Record a state update for the given peer. Note that existing state is not
     * overwritten.
     *
     * @param peerId
     * @param value
     */
    update({ peerId, value }) {
        const peerState = this.#peerStates.value[peerId];
        const existingState = peerState?.value ?? {};
        const now = Date.now();
        this.#peerStates = new PeerStateView({
            ...this.#peerStates.value,
            [peerId]: {
                peerId,
                lastSeenAt: now,
                lastActiveAt: now,
                value: {
                    ...existingState,
                    ...value,
                },
            },
        });
    }
    /**
     * Forget the given peer.
     *
     * @param peerId
     */
    delete(peerId) {
        this.#peerStates = new PeerStateView(Object.fromEntries(Object.entries(this.#peerStates.value).filter(([existingId]) => {
            return existingId != peerId;
        })));
    }
    /**
     * Prune all peers that have not been seen since the configured ttl has
     * elapsed.
     */
    prune() {
        const threshold = Date.now() - this.ttl;
        const pruned = [];
        this.#peerStates = new PeerStateView(Object.fromEntries(Object.entries(this.#peerStates.value).filter(([id, state]) => {
            const keep = state.lastSeenAt >= threshold;
            if (!keep) {
                pruned.push(id);
            }
            return keep;
        })));
        return pruned;
    }
    /**
     * Get a snapshot of the current peer states
     */
    get states() {
        return this.#peerStates;
    }
}
